/**
 * Phase 55 Plan 55-04 (Wave 2) — cost-drift pill + pure math.
 *
 * `computeMarginPct` and `marginBucket` are exported as standalone pure
 * functions (not inlined in the JSX) so Plan 55-00's RED unit tests in
 * `recipe-pure-logic-phase55.test.ts` can import and exercise them
 * directly — the threshold seams are load-bearing and cheap to test
 * without a browser.
 *
 * Thresholds (55-CONTEXT.md §Cost drift indicator):
 *   pct < 20              → red    (thin margin, warning)
 *   20 <= pct < 40        → amber  (typical)
 *   pct >= 40             → green  (healthy)
 *   null                  → gray   (insufficient purchase history)
 *
 * i.e. boundaries are LOWER-INCLUSIVE at 20 and 40 so the tests'
 * 19.9→red / 20→amber / 39.9→amber / 40→green fixtures hold exactly.
 */
import { Badge } from "@/components/ui/badge";

export function computeMarginPct(
  recipeCostTotal: string | number | null | undefined,
  sellingPrice: string | number | null | undefined,
): number | null {
  if (recipeCostTotal == null || sellingPrice == null) return null;
  const cost = Number(recipeCostTotal);
  const price = Number(sellingPrice);
  if (!Number.isFinite(cost) || !Number.isFinite(price) || price <= 0) return null;
  return ((price - cost) / price) * 100;
}

export type MarginBucket = "red" | "amber" | "green" | "gray";

export function marginBucket(pct: number | null): MarginBucket {
  if (pct == null) return "gray";
  if (pct < 20) return "red";
  if (pct < 40) return "amber";
  return "green";
}

const BUCKET_CLASSES: Record<MarginBucket, string> = {
  gray: "text-muted-foreground",
  red: "bg-destructive/10 text-destructive border-destructive/30",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  green: "border-emerald-300 bg-emerald-50 text-emerald-700",
};

export function CostDriftPill({
  recipeCostTotal,
  sellingPrice,
}: {
  recipeCostTotal: string | number | null | undefined;
  sellingPrice: string | number | null | undefined;
}) {
  const pct = computeMarginPct(recipeCostTotal, sellingPrice);
  const bucket = marginBucket(pct);
  if (bucket === "gray") {
    return (
      <Badge
        variant="outline"
        className={`text-[10px] ${BUCKET_CLASSES.gray}`}
        title="Insufficient purchase history to compute margin"
      >
        —
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={`text-[10px] font-medium tabular-nums ${BUCKET_CLASSES[bucket]}`}
    >
      {pct!.toFixed(0)}%
    </Badge>
  );
}
